/**CORE LIBRARIES IMPORT */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

/**COMPONENTS / HOOKS IMPORT */
import PhotoPlaceholder from "./PhotoPlaceholder";
import SiteImage from "./SiteImage";
import useInViewOnce from "../hooks/useInViewOnce";

/**DATA IMPORT */
import { menuCategories } from "../data/menu";

const MotionLink = motion.create(Link);

/** Categories shown as the template's DJ-style cards (display only). */
const PREVIEW = ["wood-fired-pizza", "wood-fired-panuozzo", "grill", "kebab-bowls", "burgers"];

const MenuPreview = () => {
  const [h2Ref, inView] = useInViewOnce(0.5);
  const cards = PREVIEW.map((slug) => menuCategories.find((c) => c.slug === slug)).filter(Boolean);

  return (
    <motion.section
      className="dj-section menu-preview"
      aria-labelledby="menu-preview-heading"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <h2
        id="menu-preview-heading"
        ref={h2Ref}
        className={`section-heading ${inView ? "in-view" : ""}`}
      >
        From Our Kitchen
      </h2>

      <motion.ul
        className="dj-grid"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.15,
            },
          },
        }}
      >
        {cards.map((cat) => (
          <motion.li
            key={cat.slug}
            className="dj-card"
            variants={{ hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
            whileHover="hover"
          >
            <Link to={`/menu#${cat.slug}`} className="dj-card-link">
              {/* Image */}
              <motion.div
                className="img-wrapper"
                variants={{
                  hover: { scale: 1.05 },
                }}
                transition={{ duration: 0.3 }}
              >
                <SiteImage slot={`menu_${cat.slug}`} ratio="3 / 4" fallback={<PhotoPlaceholder ratio="3 / 4" />} />
              </motion.div>

              {/* Title */}
              <motion.h3
                className="section-text"
                variants={{
                  hover: { opacity: 0 },
                }}
                transition={{ duration: 0.3 }}
              >
                {cat.name}
              </motion.h3>

              {/* Hover Box */}
              <motion.div
                className="hover-box"
                initial={{ y: "10%" }}
                variants={{
                  hover: { y: 0 },
                }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              >
                <p>{cat.name}</p>
                <p>View on our menu →</p>
              </motion.div>
            </Link>
          </motion.li>
        ))}
      </motion.ul>

      <div className="section-actions">
        <MotionLink
          to="/menu"
          className="hero-btn"
          whileHover={{ y: -6, scale: 1.05 }}
          whileTap={{ scale: 0.96 }}
        >
          View Full Menu
        </MotionLink>
      </div>
    </motion.section>
  );
};

export default MenuPreview;
